import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import DnsIcon from '@mui/icons-material/Dns'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { BackendServerOption } from '../../../all-types'
import { saveStoredBackendOptions, SELECTED_SERVER_STORAGE_KEY } from '../../../config/server-options'
import { useAppDispatch, useAppSelector } from '../../../redux-store/hooks'
import { updateClientInfo } from '../../../services'
import { BottomSaveBar } from '../components'
import { SocketConnectionBadge } from '../../../realtime'

const emptyBackendOption = (): BackendServerOption => ({
  name: '',
  url: '',
  isActive: true,
})

const normalizeUrl = (url: string) => url.trim().replace(/\/+$/, '')

const ClientBackendSettings = () => {
  const dispatcher = useAppDispatch()
  const clientInfo = useAppSelector((state) => state.clientInfo.data)
  const [backendOptions, setBackendOptions] = useState<BackendServerOption[]>(
    clientInfo.backendOptions?.length ? clientInfo.backendOptions : [emptyBackendOption()],
  )
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    setBackendOptions(clientInfo.backendOptions?.length ? clientInfo.backendOptions : [emptyBackendOption()])
  }, [clientInfo])

  const updateOption = (
    index: number,
    field: keyof BackendServerOption,
    value: string | boolean,
  ) => {
    setBackendOptions((prevOptions) =>
      prevOptions.map((option, optionIndex) =>
        optionIndex === index ? { ...option, [field]: value } : option,
      ),
    )
    setError('')
    setSuccess('')
  }

  const addOption = () => {
    setBackendOptions((prevOptions) => [...prevOptions, emptyBackendOption()])
    setError('')
    setSuccess('')
  }

  const removeOption = (index: number) => {
    setBackendOptions((prevOptions) =>
      prevOptions.length === 1
        ? [emptyBackendOption()]
        : prevOptions.filter((_, optionIndex) => optionIndex !== index),
    )
    setError('')
    setSuccess('')
  }

  const getValidatedOptions = () => {
    const normalizedOptions = backendOptions
      .map((option) => ({
        ...option,
        name: option.name.trim(),
        url: normalizeUrl(option.url),
      }))
      .filter((option) => option.name || option.url)

    if (!normalizedOptions.length) {
      throw new Error('Add at least one backend URL')
    }

    normalizedOptions.forEach((option) => {
      if (!option.name || !option.url) {
        throw new Error('Each backend needs a name and URL')
      }

      try {
        new URL(option.url)
      } catch {
        throw new Error(`Invalid URL: ${option.url}`)
      }
    })

    const hasActiveOption = normalizedOptions.some((option) => option.isActive)
    if (!hasActiveOption) {
      throw new Error('Keep at least one backend active')
    }

    return normalizedOptions
  }

  const saveOptions = async () => {
    try {
      const validatedOptions = getValidatedOptions()
      const updatedClient = {
        ...clientInfo,
        backendOptions: validatedOptions,
      }

      await dispatcher(updateClientInfo({
        clientId: updatedClient._id || '',
        body: updatedClient,
      })).unwrap()

      saveStoredBackendOptions(validatedOptions)
      setBackendOptions(validatedOptions)
      setSuccess('Backend URLs saved')
    } catch (saveError: any) {
      setError(saveError?.message || 'Could not save backend URLs')
      setSuccess('')
    }
  }

  const useServerNow = (serverUrl: string) => {
    const normalizedUrl = normalizeUrl(serverUrl)

    try {
      new URL(normalizedUrl)
      localStorage.setItem(SELECTED_SERVER_STORAGE_KEY, normalizedUrl)
      window.location.reload()
    } catch {
      setError('Enter a valid URL before using this server')
      setSuccess('')
    }
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3, width: '100%' }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography component="h2" fontWeight={800} variant="h5">
            Backend Servers
          </Typography>
          <Typography color="text.secondary">
            Manage the backend URLs shown on reload and switch servers when one is down.
          </Typography>
        </Box>

        <Box sx={{ alignSelf: 'flex-start' }}>
          <SocketConnectionBadge />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Stack spacing={2}>
          {backendOptions.map((option, index) => (
            <Paper
              key={`${option.url}-${index}`}
              elevation={2}
              sx={{ borderRadius: 2, p: 2 }}
            >
              <Stack alignItems="center" direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <DnsIcon color={option.isActive ? 'primary' : 'disabled'} />
                <TextField
                  label="Name"
                  onChange={(event) => updateOption(index, 'name', event.target.value)}
                  value={option.name}
                  fullWidth
                />
                <TextField
                  label="Backend URL"
                  onChange={(event) => updateOption(index, 'url', event.target.value)}
                  placeholder="https://api.example.com"
                  value={option.url}
                  fullWidth
                />
                <Stack alignItems="center" direction="row" spacing={0.5}>
                  <Typography variant="body2">Active</Typography>
                  <Switch
                    checked={option.isActive}
                    onChange={(event) => updateOption(index, 'isActive', event.target.checked)}
                  />
                </Stack>
                <Tooltip title="Use now">
                  <span>
                    <IconButton
                      color="primary"
                      disabled={!option.url.trim()}
                      onClick={() => useServerNow(option.url)}
                    >
                      <PlayArrowIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove">
                  <IconButton color="error" onClick={() => removeOption(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Paper>
          ))}
        </Stack>

        <Button
          onClick={addOption}
          startIcon={<AddIcon />}
          sx={{ alignSelf: 'flex-start' }}
          variant="outlined"
        >
          Add Backend
        </Button>
      </Stack>

      <BottomSaveBar onSave={saveOptions} />
    </Box>
  )
}

export { ClientBackendSettings }
